import React from "react";

function Footer() {
  // ✅ Essential links only - curated for clarity
  const essentialLinks = {
    company: [
      { label: "About Us", href: "/about.html" },
      { label: "Contact", href: "/contact.html" },
    ],
    legal: [
      { label: "Terms & Conditions", href: "/terms.html" },
      // { label: "Privacy Policy", href: "/privacy.html" },
      { label: "FAQ", href: "/faq.html" },
    ],
    platform: [
      { label: "Investment Plans", href: "/deposit" },
    ],
  };

  return (
    <>
      {/* Footer Styles */}
      <style>{`
        :root {
          --footer-bg: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
          --footer-text: #a1a39e;
          --footer-text-hover: #FE413C;
          --footer-accent: #D4AF37;
          --footer-border: rgba(255, 255, 255, 0.08);
        }
        
        .footer-wrapper {
          background: var(--footer-bg);
          color: var(--footer-text);
          font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          position: relative;
          overflow: hidden;
        }
        
        /* Subtle gradient overlay */
        .footer-wrapper::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, var(--footer-accent), transparent);
          opacity: 0.6;
        }
        
        .footer-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 3rem 1.5rem 2rem;
        }
        
        /* Footer Grid */
        .footer-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2rem;
          margin-bottom: 2.5rem;
        }
        
        .footer-section h4 {
          font-size: 0.95rem;
          font-weight: 600;
          color: #fff;
          margin-bottom: 1rem;
          letter-spacing: 0.03em;
          position: relative;
          padding-bottom: 0.5rem;
        }
        
        .footer-section h4::after {
          content: "";
          position: absolute;
          bottom: 0;
          left: 0;
          width: 32px;
          height: 2px;
          background: var(--footer-accent);
          border-radius: 1px;
        }
        
        .footer-links {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .footer-links li {
          margin-bottom: 0.5rem;
        }
        
        .footer-links a {
          color: var(--footer-text);
          text-decoration: none;
          font-size: 0.9rem;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.4rem;
        }
        
        .footer-links a::before {
          content: "▹";
          color: var(--footer-accent);
          font-size: 0.8rem;
          opacity: 0.8;
          transition: transform 0.2s ease;
        }
        
        .footer-links a:hover {
          color: var(--footer-text-hover);
          transform: translateX(3px);
        }
        
        .footer-links a:hover::before {
          transform: translateX(2px);
        }
        
        /* Address Section */
        .footer-address {
          background: rgba(255, 255, 255, 0.04);
          border-radius: 12px;
          padding: 1.25rem;
          border: 1px solid var(--footer-border);
        }
        
        .footer-address h4 {
          margin-bottom: 0.75rem;
        }
        
        .address-content {
          font-size: 0.9rem;
          line-height: 1.6;
          color: var(--footer-text);
        }
        
        .address-content strong {
          color: #fff;
          font-weight: 500;
        }
        
        .address-content a {
          color: var(--footer-accent);
          text-decoration: none;
          transition: color 0.2s ease;
        }
        
        .address-content a:hover {
          color: var(--footer-text-hover);
          text-decoration: underline;
        }
        
        /* Bottom Bar */
        .footer-bottom {
          border-top: 1px solid var(--footer-border);
          padding-top: 1.5rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        
        .payment-methods {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex-wrap: wrap;
          justify-content: center;
        }
        
        .payment-methods img {
          height: 28px;
          opacity: 0.8;
          transition: opacity 0.2s ease;
          filter: grayscale(100%) brightness(1.2);
        }
        
        .payment-methods img:hover {
          opacity: 1;
          filter: none;
        }
        
        .footer-credit {
          font-size: 0.85rem;
          color: var(--footer-text);
          text-align: center;
          margin: 0;
        }
        
        .footer-credit strong {
          color: var(--footer-accent);
        }
        
        /* Crypto Ticker */
        .crypto-ticker {
          position: fixed;
          left: 0;
          bottom: 0;
          right: 0;
          z-index: 100;
          background: #fff;
          box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
        }
        
        .crypto-ticker iframe {
          display: block;
          width: 100%;
          height: 40px;
          border: none;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .footer-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .footer-container {
            padding: 2rem 1rem 1.5rem;
          }
          
          .footer-bottom {
            flex-direction: column;
            text-align: center;
          }
        }
        
        @media (max-width: 480px) {
          .footer-section h4 {
            font-size: 0.9rem;
          }
          
          .footer-links a {
            font-size: 0.85rem;
          }
          
          .address-content {
            font-size: 0.85rem;
          }
        }
      `}</style>

      <footer className="footer-wrapper">
        <div className="footer-container">
          <div className="footer-grid">
            
            {/* Company Section */}
            <div className="footer-section">
              <h4>Company</h4>
              <ul className="footer-links">
                {essentialLinks.company.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Platform Section */}
            <div className="footer-section">
              <h4>Platform</h4>
              <ul className="footer-links">
                {essentialLinks.platform.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Section */}
            <div className="footer-section">
              <h4>Legal</h4>
              <ul className="footer-links">
                {essentialLinks.legal.map((link) => (
                  <li key={link.href}>
                    <a href={link.href}>{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Address Section */}
            <div className="footer-section">
              <h4>Office</h4>
              <div className="footer-address">
                <div className="address-content">
                  <strong>APEXYEILD Pty Ltd</strong><br />
                  Unit 7/4 Queen St<br />
                  Bentley, WA 6102<br />
                  Australia
                  <br /><br />
                  <a href="mailto:support@apexyeild.com">support@apexyeild.com</a>
                </div>
              </div>
            </div>

          </div>

          {/* Bottom Bar */}
          <div className="footer-bottom">
            <p className="footer-credit">
              © <strong>{new Date().getFullYear()}</strong> APEXYEILD. All Rights Reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Crypto Ticker - Fixed Bottom */}
      <div className="crypto-ticker">
        <iframe
          src="https://widget.coinlib.io/widget?type=horizontal_v2&theme=light&pref_coin_id=1505&invert_hover="
          title="Crypto Prices"
          loading="lazy"
        />
      </div>
    </>
  );
}

export default Footer;